import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { updateSetting } from "../../../services/settingsService";
import { downloadColorCSS, applyColorsToPage, checkAccessibility } from "./ColorUtils";
import { getSettings } from "../../../services/settingsService";

const Colors = ({ colors, setColors }) => {
    const [localColors, setLocalColors] = useState({
        primary_color: '#007bff',
        secondary_color: '#6c757d',
        success_color: '#28a745',
        danger_color: '#dc3545',
        warning_color: '#ffc107',
        info_color: '#17a2b8',
        light_color: '#f8f9fa',
        dark_color: '#343a40',
        text_color: '#212529',
        text_light_color: '#6c757d',
        background_color: '#ffffff',
        background_light_color: '#f8f9fa',
        border_color: '#dee2e6',
        link_color: '#007bff',
        link_hover_color: '#0056b3',
        button_primary_color: '#007bff',
        button_primary_hover_color: '#0056b3',
        button_secondary_color: '#6c757d',
        button_secondary_hover_color: '#545b62',
        header_background_color: '#ffffff',
        header_text_color: '#212529',
        footer_background_color: '#343a40',
        footer_text_color: '#ffffff',
        sidebar_background_color: '#f8f9fa',
        sidebar_text_color: '#212529',
        card_background_color: '#ffffff',
        card_border_color: '#dee2e6',
        input_border_color: '#ced4da',
        input_focus_color: '#80bdff',
        shadow_color: 'rgba(0,0,0,0.15)',
        gradient_start_color: '#007bff',
        gradient_end_color: '#0056b3'
    });

    useEffect(() => {
        if (colors && Object.keys(colors).length > 0) {
            setLocalColors(prev => ({
                ...prev,
                ...colors
            }));
        }
    }, [colors]);

    useEffect(() => {
        getSettings().then(settings => {
            console.log(settings);
            setColors(settings);
        });
    }, []);

    const handleColorChange = (colorKey, value) => {
        const updatedColors = {
            ...localColors,
            [colorKey]: value
        };
        setLocalColors(updatedColors);
        setColors(updatedColors);
    };

    const handleSave = async () => {
        try {
            const updates = Object.keys(localColors).map(key =>
                updateSetting(key, localColors[key])
            );

            const results = await Promise.all(updates);
            const allSuccessful = results.every(result => result);

            if (allSuccessful) {
                // Apply colors to current page
                applyColorsToPage(localColors);
                toast.success("Color settings saved successfully!");
            } else {
                toast.error("Some color settings failed to save");
            }
        } catch (error) {
            console.error("Error saving color settings:", error);
            toast.error("Failed to save color settings");
        }
    };

    const handleDownloadCSS = () => {
        try {
            downloadColorCSS(localColors, 'custom-colors.css');
            toast.success("CSS file downloaded successfully!");
        } catch (error) {
            console.error("Error downloading CSS:", error);
            toast.error("Failed to download CSS file");
        }
    };

    const handlePreview = () => {
        try {
            applyColorsToPage(localColors);
            toast.info("Colors applied to current page for preview");
        } catch (error) {
            console.error("Error applying colors:", error);
            toast.error("Failed to apply color preview");
        }
    };

    const resetToDefault = () => {
        const defaultColors = {
            primary_color: '#007bff',
            secondary_color: '#6c757d',
            success_color: '#28a745',
            danger_color: '#dc3545',
            warning_color: '#ffc107',
            info_color: '#17a2b8',
            light_color: '#f8f9fa',
            dark_color: '#343a40',
            text_color: '#212529',
            text_light_color: '#6c757d',
            background_color: '#ffffff',
            background_light_color: '#f8f9fa',
            border_color: '#dee2e6',
            link_color: '#007bff',
            link_hover_color: '#0056b3',
            button_primary_color: '#007bff',
            button_primary_hover_color: '#0056b3',
            button_secondary_color: '#6c757d',
            button_secondary_hover_color: '#545b62',
            header_background_color: '#ffffff',
            header_text_color: '#212529',
            footer_background_color: '#343a40',
            footer_text_color: '#ffffff',
            sidebar_background_color: '#f8f9fa',
            sidebar_text_color: '#212529',
            card_background_color: '#ffffff',
            card_border_color: '#dee2e6',
            input_border_color: '#ced4da',
            input_focus_color: '#80bdff',
            shadow_color: 'rgba(0,0,0,0.15)',
            gradient_start_color: '#007bff',
            gradient_end_color: '#0056b3'
        };
        setLocalColors(defaultColors);
        setColors(defaultColors);
        toast.info("Colors reset to default");
    };

    const colorGroups = [
        {
            title: "Primary Colors",
            colors: [
                { key: 'primary_color', label: 'Primary Color', description: 'Main brand color' },
                { key: 'secondary_color', label: 'Secondary Color', description: 'Secondary brand color' },
                { key: 'success_color', label: 'Success Color', description: 'Success messages and elements' },
                { key: 'danger_color', label: 'Danger Color', description: 'Error messages and warnings' },
                { key: 'warning_color', label: 'Warning Color', description: 'Warning messages' },
                { key: 'info_color', label: 'Info Color', description: 'Information messages' }
            ]
        },
        {
            title: "Text Colors",
            colors: [
                { key: 'text_color', label: 'Text Color', description: 'Main text color' },
                { key: 'text_light_color', label: 'Light Text Color', description: 'Secondary text color' },
                { key: 'link_color', label: 'Link Color', description: 'Link text color' },
                { key: 'link_hover_color', label: 'Link Hover Color', description: 'Link hover color' }
            ]
        },
        {
            title: "Background Colors",
            colors: [
                { key: 'background_color', label: 'Background Color', description: 'Main background color' },
                { key: 'background_light_color', label: 'Light Background', description: 'Light background color' },
                { key: 'card_background_color', label: 'Card Background', description: 'Card background color' }
            ]
        },
        {
            title: "Button Colors",
            colors: [
                { key: 'button_primary_color', label: 'Primary Button', description: 'Primary button color' },
                { key: 'button_primary_hover_color', label: 'Primary Button Hover', description: 'Primary button hover color' },
                { key: 'button_secondary_color', label: 'Secondary Button', description: 'Secondary button color' },
                { key: 'button_secondary_hover_color', label: 'Secondary Button Hover', description: 'Secondary button hover color' }
            ]
        },
        {
            title: "Layout Colors",
            colors: [
                { key: 'header_background_color', label: 'Header Background', description: 'Header background color' },
                { key: 'header_text_color', label: 'Header Text', description: 'Header text color' },
                { key: 'footer_background_color', label: 'Footer Background', description: 'Footer background color' },
                { key: 'footer_text_color', label: 'Footer Text', description: 'Footer text color' },
                { key: 'sidebar_background_color', label: 'Sidebar Background', description: 'Sidebar background color' },
                { key: 'sidebar_text_color', label: 'Sidebar Text', description: 'Sidebar text color' }
            ]
        },
        {
            title: "Form Colors",
            colors: [
                { key: 'input_border_color', label: 'Input Border', description: 'Input field border color' },
                { key: 'input_focus_color', label: 'Input Focus', description: 'Input focus border color' },
                { key: 'border_color', label: 'Border Color', description: 'General border color' }
            ]
        },
        {
            title: "Special Effects",
            colors: [
                { key: 'shadow_color', label: 'Shadow Color', description: 'Box shadow color' },
                { key: 'gradient_start_color', label: 'Gradient Start', description: 'Gradient start color' },
                { key: 'gradient_end_color', label: 'Gradient End', description: 'Gradient end color' }
            ]
        }
    ];

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-yourdetails dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Website Color Settings</h3>
                        <p>Customize the color scheme of your website</p>
                    </div>

                    <div className="dc-formtheme dc-userform">
                        {colorGroups.map((group, groupIndex) => (
                            <div key={groupIndex} className="color-group" style={{ marginBottom: '30px' }}>
                                <h4 style={{
                                    color: localColors.text_color,
                                    borderBottom: `2px solid ${localColors.primary_color}`,
                                    paddingBottom: '10px',
                                    marginBottom: '20px'
                                }}>
                                    {group.title}
                                </h4>

                                <div className="row">
                                    {group.colors.map((colorItem, index) => (
                                        <div key={index} className="col-md-6 col-lg-4" style={{ marginBottom: '20px' }}>
                                            <div className="color-picker-item" style={{
                                                padding: '15px',
                                                border: `1px solid ${localColors.border_color}`,
                                                borderRadius: '8px',
                                                backgroundColor: localColors.card_background_color
                                            }}>
                                                <div className="column">
                                                    <div >
                                                        <label style={{
                                                            color: localColors.text_color,
                                                            fontWeight: 'bold',
                                                            marginBottom: '5px',
                                                            display: 'block'
                                                        }}>
                                                            {colorItem.label}
                                                        </label>

                                                        <p style={{
                                                            color: localColors.text_light_color,
                                                            fontSize: '12px',
                                                            marginBottom: '10px'
                                                        }}>
                                                            {colorItem.description}
                                                        </p>
                                                    </div>
                                                    <div style={{ display: "inline-block" }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <input
                                                                type="color"
                                                                value={localColors[colorItem.key]}
                                                                onChange={(e) => handleColorChange(colorItem.key, e.target.value)}
                                                                style={{
                                                                    width: '50px',
                                                                    height: '40px',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            />
                                                            <input
                                                                type="text"
                                                                value={localColors[colorItem.key]}
                                                                onChange={(e) => handleColorChange(colorItem.key, e.target.value)}
                                                                style={{
                                                                    flex: "auto",
                                                                    padding: '8px',
                                                                    border: `1px solid ${localColors.input_border_color}`,
                                                                    borderRadius: '4px',
                                                                    backgroundColor: localColors.background_color,
                                                                    color: localColors.text_color,
                                                                    width: '0%'
                                                                }}
                                                                placeholder="Enter color code"
                                                            />
                                                        </div>
                                                    </div>

                                                </div>


                                                <div style={{
                                                    marginTop: '8px',
                                                    padding: '8px',
                                                    backgroundColor: localColors[colorItem.key],
                                                    borderRadius: '4px',
                                                    textAlign: 'center',
                                                    color: localColors[colorItem.key] === '#ffffff' || localColors[colorItem.key] === '#f8f9fa' ? '#000000' : '#ffffff',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    Preview
                                                </div>

                                                {/* Accessibility Check for text/background combinations */}
                                                {(colorItem.key.includes('text') || colorItem.key.includes('header_text') || colorItem.key.includes('footer_text') || colorItem.key.includes('sidebar_text')) && (
                                                    <div style={{ marginTop: '5px', fontSize: '10px' }}>
                                                        {(() => {
                                                            const bgKey = colorItem.key.replace('text', 'background').replace('_text', '_background');
                                                            const bgColor = localColors[bgKey] || localColors.background_color;
                                                            const accessibility = checkAccessibility(localColors[colorItem.key], bgColor);
                                                            return (
                                                                <span style={{
                                                                    color: accessibility.aa ? '#28a745' : '#dc3545',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {accessibility.level} ({accessibility.ratio.toFixed(2)})
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div className="form-actions" style={{
                            marginTop: '30px',
                            padding: '20px',
                            borderTop: `1px solid ${localColors.border_color}`,
                            textAlign: 'right'
                        }}>
                            <Button
                                variant="outline-secondary"
                                onClick={resetToDefault}
                                style={{
                                    marginRight: '10px',
                                    borderColor: localColors.border_color,
                                    color: localColors.text_color
                                }}
                            >
                                Reset to Default
                            </Button>
                            <Button
                                variant="outline-info"
                                onClick={handlePreview}
                                style={{
                                    marginRight: '10px',
                                    borderColor: localColors.info_color,
                                    color: localColors.info_color
                                }}
                            >
                                Preview Colors
                            </Button>
                            <Button
                                variant="outline-success"
                                onClick={handleDownloadCSS}
                                style={{
                                    marginRight: '10px',
                                    borderColor: localColors.success_color,
                                    color: localColors.success_color
                                }}
                            >
                                Download CSS
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                style={{
                                    backgroundColor: localColors.button_primary_color,
                                    borderColor: localColors.button_primary_color
                                }}
                            >
                                Save Color Settings
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Color Preview Section */}
                <div className="dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Color Preview</h3>
                        <p>See how your color scheme looks</p>
                    </div>

                    <div className="color-preview" style={{
                        padding: '20px',
                        border: `1px solid ${localColors.border_color}`,
                        borderRadius: '8px',
                        backgroundColor: localColors.background_color
                    }}>
                        <div style={{
                            backgroundColor: localColors.header_background_color,
                            color: localColors.header_text_color,
                            padding: '15px',
                            borderRadius: '4px',
                            marginBottom: '15px'
                        }}>
                            <h4 style={{ margin: 0 }}>Header Preview</h4>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <h5 style={{ color: localColors.text_color }}>Sample Text</h5>
                            <p style={{ color: localColors.text_light_color }}>
                                This is how your text will look with the selected colors.
                            </p>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <a href="#" style={{
                                color: localColors.link_color,
                                textDecoration: 'none',
                                marginRight: '15px'
                            }}>
                                Sample Link
                            </a>
                            <span style={{ color: localColors.text_light_color }}>Normal text</span>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <button style={{
                                backgroundColor: localColors.button_primary_color,
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                marginRight: '10px',
                                cursor: 'pointer'
                            }}>
                                Primary Button
                            </button>
                            <button style={{
                                backgroundColor: localColors.button_secondary_color,
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}>
                                Secondary Button
                            </button>
                        </div>

                        <div style={{
                            backgroundColor: localColors.card_background_color,
                            border: `1px solid ${localColors.card_border_color}`,
                            borderRadius: '4px',
                            padding: '15px'
                        }}>
                            <h6 style={{ color: localColors.text_color, margin: '0 0 10px 0' }}>Card Preview</h6>
                            <p style={{ color: localColors.text_light_color, margin: 0 }}>
                                This is how cards will look with your color scheme.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Colors;
